import {
    Box,
    VStack,
    Flex,
    Text,
    Button,
    Icon,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Spinner,
    Tooltip,
} from '@chakra-ui/react';
import { TbCalendarUp, TbFileInvoice, TbSortAscending, TbSortDescending, TbCalendar, TbChevronLeft, TbBuildingStore, TbFlag, TbChartDonut, TbInfoCircle } from 'react-icons/tb';
import { efficiencyBreakdownTooltipLabel } from '../../shared/efficiencyBreakdownInfo';
import { IconType } from 'react-icons';
import { useState, useEffect, useMemo, useRef } from 'react';
import { InvoiceAdditionalFilters, useInvoiceVendorOptions } from '../../hooks/invoice/useInvoicesFeed';
import DatePicker from '../DatePicker';
import { AddFilterMenu, ClearFiltersButton, FilterBar, FilterChip } from '../../ui';
import {
    CheckboxMenuSearch,
    CheckboxMenuList,
    CheckboxMenuRow,
    MenuRow,
    MenuFooterActions,
    SelectionSummaryTooltip,
    checkboxMenuColors,
    checkboxMenuPopoverContentSx,
} from '../../ui/primitives/CheckboxMenu';
import { INVOICE_REASON_FLAG_CONFIG, type InvoiceReasonFlagKey } from './invoiceReasonFlagConfig';
import { getInvoiceNexIdentifierUi, type NexReconciliationIdentifierMode } from '../../utils/invoiceNexIdentifierUi';

export interface InvoiceAdvancedFiltersProps {
    filters: InvoiceAdditionalFilters;
    onFiltersChange: (filters: InvoiceAdditionalFilters) => void;
    locationIds: Array<string> | 'all';
    /** Resets the shop location selector. Clear filters calls this with the other filters. */
    onClearLocations?: () => void;
    feedType: 'all' | 'flagged' | 'submitted';
    archivedFilter: 'active' | 'archived' | 'all';
    nexReconciliationIdentifierMode?: NexReconciliationIdentifierMode;
}

type FilterType = 'uploadDate' | 'invoiceType' | 'vendor' | 'flaggedReasons' | 'efficiencyFlags';
type OpenState = 'addFilter' | FilterType | null;

const FILTER_CATEGORIES: { key: FilterType; label: string; icon: IconType }[] = [
    { key: 'uploadDate', label: 'Upload Date', icon: TbCalendarUp },
    { key: 'invoiceType', label: 'Invoice Type', icon: TbFileInvoice },
    { key: 'vendor', label: 'Vendor', icon: TbBuildingStore },
    { key: 'flaggedReasons', label: 'Flagged Reasons', icon: TbFlag },
    { key: 'efficiencyFlags', label: 'Efficiency', icon: TbChartDonut },
];

const EFFICIENCY_FLAG_OPTIONS = [
    { value: 'theirFault' as const, label: 'Shop-side flags' },
    { value: 'neededFlags' as const, label: 'Needed flags' },
    { value: 'match' as const, label: 'Yaxle-side flags' },
];

const FLAGGED_REASON_OPTIONS = Object.values(INVOICE_REASON_FLAG_CONFIG);

/** Chip copy: spell out up to two selections, then collapse to a count. */
const formatMultiChip = (prefix: string, labels: string[]) =>
    `${prefix}: ${labels.length <= 2 ? labels.join(', ') : `${labels.length} selected`}`;

const INVOICE_TYPE_FILTER_OPTIONS = [
    { value: 'received' as const, label: 'Received' },
    { value: 'returned' as const, label: 'Credited' },
    { value: 'return_slip' as const, label: 'Return Slip' },
    { value: 'sublet' as const, label: 'Sublet' },
];

const invoiceTypeFilterLabel = (value: (typeof INVOICE_TYPE_FILTER_OPTIONS)[number]['value']) =>
    INVOICE_TYPE_FILTER_OPTIONS.find((option) => option.value === value)?.label ?? value;

function EfficiencyBreakdownInfoIcon() {
    return (
        <Tooltip
            label={efficiencyBreakdownTooltipLabel()}
            placement="right"
            hasArrow
            maxW="360px"
        >
            <Box
                as="span"
                display="inline-flex"
                color="gray.400"
                cursor="help"
                lineHeight={0}
                ml="6px"
                _hover={{ color: 'gray.600' }}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />
            </Box>
        </Tooltip>
    );
}

const InvoiceAdvancedFiltersComponent = ({
    filters,
    onFiltersChange,
    locationIds,
    onClearLocations,
    feedType,
    archivedFilter,
    nexReconciliationIdentifierMode,
}: InvoiceAdvancedFiltersProps) => {
    const identifierUi = getInvoiceNexIdentifierUi(nexReconciliationIdentifierMode);

    // UI state
    const [openState, setOpenState] = useState<OpenState>(null);
    const [addFilterView, setAddFilterView] = useState<FilterType | null>(null);
    const [tempFilters, setTempFilters] = useState<InvoiceAdditionalFilters>(filters);
    const [filterSearch, setFilterSearch] = useState('');
    const [vendorSearch, setVendorSearch] = useState('');

    const containerRef = useRef<HTMLDivElement>(null);

    // Vendor options are fetched lazily, only while the vendor panel is open.
    const vendorFilterOpen =
        openState === 'vendor' || (openState === 'addFilter' && addFilterView === 'vendor');
    const { data: vendorOptions = [], isFetching: isVendorsLoading } = useInvoiceVendorOptions(
        feedType,
        archivedFilter,
        locationIds,
        vendorFilterOpen,
    );

    const flaggedReasonLabel = (key: InvoiceReasonFlagKey): string =>
        key === 'ro' ? identifierUi.reasonBadgeLabel : INVOICE_REASON_FLAG_CONFIG[key].label;

    // Close popover and reset
    const closePopover = () => {
        setOpenState(null);
        setAddFilterView(null);
        setFilterSearch('');
        setVendorSearch('');
    };

    // Toggle popover
    const togglePopover = (target: OpenState) => {
        if (openState === target) {
            closePopover();
        } else {
            setTempFilters({ ...filters });
            setFilterSearch('');
            setVendorSearch('');
            if (target === 'addFilter') {
                setAddFilterView(null);
            }
            setOpenState(target);
        }
    };

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!openState) return;
            const target = event.target as HTMLElement;
            if (containerRef.current?.contains(target)) return;
            if (target.closest('[data-filter-popover]')) return;
            closePopover();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openState]);

    // --- Temp filter handlers ---
    const handleTempUploadDateSort = (value: 'asc' | 'desc' | 'range') => {
        setTempFilters((prev: InvoiceAdditionalFilters) => {
            const newFilters = { ...prev };
            if (prev.uploadDateSort === value) {
                delete newFilters.uploadDateSort;
                delete newFilters.uploadDateRange;
            } else {
                newFilters.uploadDateSort = value;
                if (value !== 'range') {
                    delete newFilters.uploadDateRange;
                }
            }
            return newFilters;
        });
    };

    const handleTempUploadDateRange = (start: Date, end: Date) => {
        setTempFilters((prev: InvoiceAdditionalFilters) => ({ ...prev, uploadDateRange: { start, end } }));
    };

    const handleTempInvoiceType = (value: 'received' | 'returned' | 'return_slip' | 'sublet') => {
        setTempFilters((prev: InvoiceAdditionalFilters) => {
            const current = prev.invoiceTypes?.length
                ? prev.invoiceTypes
                : prev.invoiceType
                    ? [prev.invoiceType]
                    : [];
            const next = current.includes(value)
                ? current.filter((type) => type !== value)
                : [...current, value];
            const newFilters = { ...prev };
            delete newFilters.invoiceType;
            if (next.length > 0) newFilters.invoiceTypes = next;
            else delete newFilters.invoiceTypes;
            return newFilters;
        });
    };

    const toggleVendor = (name: string) => {
        setTempFilters((prev) => {
            const current = prev.vendorNames ?? [];
            const exists = current.includes(name);
            const next = exists ? current.filter((n) => n !== name) : [...current, name];
            const newFilters = { ...prev };
            if (next.length > 0) newFilters.vendorNames = next;
            else delete newFilters.vendorNames;
            return newFilters;
        });
    };

    const toggleFlaggedReason = (key: InvoiceReasonFlagKey) => {
        setTempFilters((prev) => {
            const current = prev.flaggedReasons ?? [];
            const exists = current.includes(key);
            const next = exists ? current.filter((k) => k !== key) : [...current, key];
            const newFilters = { ...prev };
            if (next.length > 0) newFilters.flaggedReasons = next;
            else {
                delete newFilters.flaggedReasons;
                delete newFilters.flaggedReasonsMatch;
            }
            return newFilters;
        });
    };

    const toggleEfficiencyFlag = (value: (typeof EFFICIENCY_FLAG_OPTIONS)[number]['value']) => {
        setTempFilters((prev) => {
            const current = prev.efficiencyFlags ?? [];
            const next = current.includes(value)
                ? current.filter((flag) => flag !== value)
                : [...current, value];
            const newFilters = { ...prev };
            if (next.length > 0) newFilters.efficiencyFlags = next;
            else delete newFilters.efficiencyFlags;
            return newFilters;
        });
    };

    const setFlaggedReasonsMatch = (mode: 'any' | 'exact') => {
        setTempFilters((prev) => {
            const newFilters = { ...prev };
            if (mode === 'exact') newFilters.flaggedReasonsMatch = 'exact';
            else delete newFilters.flaggedReasonsMatch;
            return newFilters;
        });
    };

    // Apply
    const handleApply = () => {
        onFiltersChange(tempFilters);
        closePopover();
    };

    // Remove specific filter
    const removeFilter = (filterType: FilterType) => {
        const newFilters = { ...filters };
        switch (filterType) {
            case 'uploadDate': delete newFilters.uploadDateSort; delete newFilters.uploadDateRange; break;
            case 'invoiceType':
                delete newFilters.invoiceType;
                delete newFilters.invoiceTypes;
                break;
            case 'vendor': delete newFilters.vendorNames; break;
            case 'flaggedReasons':
                delete newFilters.flaggedReasons;
                delete newFilters.flaggedReasonsMatch;
                break;
            case 'efficiencyFlags':
                delete newFilters.efficiencyFlags;
                break;
        }
        onFiltersChange(newFilters);
        if (openState === filterType) closePopover();
    };

    const clearAllFilters = () => {
        onFiltersChange({});
        onClearLocations?.();
        closePopover();
    };

    // --- Computed values ---
    const isFilterActive = (filterType: FilterType): boolean => {
        switch (filterType) {
            case 'uploadDate': return !!filters.uploadDateSort;
            case 'invoiceType':
                return (filters.invoiceTypes?.length ?? 0) > 0 || !!filters.invoiceType;
            case 'vendor': return (filters.vendorNames?.length ?? 0) > 0;
            case 'flaggedReasons': return (filters.flaggedReasons?.length ?? 0) > 0;
            case 'efficiencyFlags': return (filters.efficiencyFlags?.length ?? 0) > 0;
        }
    };

    const getFilterLabel = (filterType: FilterType): string => {
        switch (filterType) {
            case 'uploadDate': {
                const sortLabel = filters.uploadDateSort === 'asc' ? 'Ascending' :
                    filters.uploadDateSort === 'desc' ? 'Descending' : 'Date Range';
                return `Upload Date: ${sortLabel}`;
            }
            case 'invoiceType': {
                const selected = filters.invoiceTypes?.length
                    ? filters.invoiceTypes
                    : filters.invoiceType
                        ? [filters.invoiceType]
                        : [];
                const labels = selected.map(invoiceTypeFilterLabel);
                return formatMultiChip('Invoice Type', labels);
            }
            case 'vendor': {
                const labels = (filters.vendorNames ?? []).map((name) => name || 'No vendor');
                return formatMultiChip('Vendor', labels);
            }
            case 'flaggedReasons': {
                const labels = (filters.flaggedReasons ?? []).map(flaggedReasonLabel);
                const joined = labels.length <= 2 ? labels.join(', ') : `${labels.length} selected`;
                return filters.flaggedReasonsMatch === 'exact'
                    ? `Flagged Reasons: only ${joined}`
                    : `Flagged Reasons: ${joined}`;
            }
            case 'efficiencyFlags': {
                const labels = EFFICIENCY_FLAG_OPTIONS
                    .filter((option) => (filters.efficiencyFlags ?? []).includes(option.value))
                    .map((option) => option.label);
                return formatMultiChip('Efficiency', labels);
            }
        }
    };

    /** Names behind a collapsed "N selected" chip — shown in the hover panel. */
    const getFilterTooltipItems = (filterType: FilterType): string[] => {
        const names = (() => {
            switch (filterType) {
                case 'vendor':
                    return (filters.vendorNames ?? []).map((name) => name || 'No vendor');
                case 'invoiceType': {
                    const selected = filters.invoiceTypes?.length
                        ? filters.invoiceTypes
                        : filters.invoiceType
                            ? [filters.invoiceType]
                            : [];
                    return selected.map(invoiceTypeFilterLabel);
                }
                case 'flaggedReasons':
                    return (filters.flaggedReasons ?? []).map(flaggedReasonLabel);
                case 'efficiencyFlags':
                    return EFFICIENCY_FLAG_OPTIONS
                        .filter((option) => (filters.efficiencyFlags ?? []).includes(option.value))
                        .map((option) => option.label);
                default:
                    return [];
            }
        })();
        return names.length > 2 ? names : [];
    };

    /** Value preview on a drill-in row — the chip label without its category prefix. */
    const getCategorySummary = (filterType: FilterType): string | undefined => {
        if (!isFilterActive(filterType)) return undefined;
        const label = getFilterLabel(filterType);
        const separator = label.indexOf(': ');
        return separator === -1 ? label : label.slice(separator + 2);
    };

    const activeFilters = FILTER_CATEGORIES.filter(cat => isFilterActive(cat.key));
    const locationFilterActive = locationIds !== 'all' && locationIds.length > 0;

    const selectedVendorNames = tempFilters.vendorNames ?? [];
    const selectedFlaggedReasons = tempFilters.flaggedReasons ?? [];

    // Selected vendors pin to the top even when the feed no longer returns them.
    const vendorPickerOptions = useMemo(() => {
        const selected = tempFilters.vendorNames ?? [];
        const seen = new Set(vendorOptions);
        const extras = selected.filter((name) => !seen.has(name));
        return [...extras, ...vendorOptions];
    }, [vendorOptions, tempFilters.vendorNames]);

    const filteredVendorOptions = useMemo(() => {
        const query = vendorSearch.trim().toLowerCase();
        if (!query) return vendorPickerOptions;
        return vendorPickerOptions.filter((name) => {
            const label = (name || 'No vendor').toLowerCase();
            return label.includes(query);
        });
    }, [vendorPickerOptions, vendorSearch]);

    // --- Sub-components ---
    const FilterOption = ({ label, isSelected, onClick, icon }: { label: string; isSelected: boolean; onClick: () => void; icon?: IconType }) => (
        <MenuRow
            kind="single"
            isSelected={isSelected}
            onClick={onClick}
            icon={icon ? <Icon as={icon as React.ElementType} boxSize={4} /> : undefined}
        >
            {label}
        </MenuRow>
    );

    // Render filter-specific options
    const renderFilterOptions = (filterType: FilterType) => {
        switch (filterType) {
            case 'uploadDate':
                return (
                    <VStack align="stretch" spacing={0} p="6px">
                        <FilterOption
                            label="Ascending"
                            icon={TbSortAscending}
                            isSelected={tempFilters.uploadDateSort === 'asc'}
                            onClick={() => handleTempUploadDateSort('asc')}
                        />
                        <FilterOption
                            label="Descending"
                            icon={TbSortDescending}
                            isSelected={tempFilters.uploadDateSort === 'desc'}
                            onClick={() => handleTempUploadDateSort('desc')}
                        />
                        <FilterOption
                            label="Date Range"
                            icon={TbCalendar}
                            isSelected={tempFilters.uploadDateSort === 'range'}
                            onClick={() => handleTempUploadDateSort('range')}
                        />
                        {tempFilters.uploadDateSort === 'range' && (
                            <Box px={3} py={2}>
                                <VStack spacing={2} align="stretch">
                                    <Box>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>START DATE</Text>
                                        <DatePicker
                                            value={tempFilters.uploadDateRange?.start || new Date()}
                                            onChange={(date: Date) => {
                                                handleTempUploadDateRange(date, tempFilters.uploadDateRange?.end || new Date());
                                            }}
                                            size="sm"
                                        />
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>END DATE</Text>
                                        <DatePicker
                                            value={tempFilters.uploadDateRange?.end || new Date()}
                                            onChange={(date: Date) => {
                                                handleTempUploadDateRange(tempFilters.uploadDateRange?.start || new Date(), date);
                                            }}
                                            size="sm"
                                        />
                                    </Box>
                                </VStack>
                            </Box>
                        )}
                    </VStack>
                );

            case 'invoiceType':
                return (
                    <CheckboxMenuList maxH="220px">
                        {INVOICE_TYPE_FILTER_OPTIONS.map((option) => (
                            <CheckboxMenuRow
                                key={option.value}
                                isSelected={(tempFilters.invoiceTypes ?? (tempFilters.invoiceType ? [tempFilters.invoiceType] : [])).includes(option.value)}
                                onClick={() => handleTempInvoiceType(option.value)}
                            >
                                {option.label}
                            </CheckboxMenuRow>
                        ))}
                    </CheckboxMenuList>
                );

            case 'vendor':
                return (
                    <>
                        <CheckboxMenuSearch
                            value={vendorSearch}
                            onChange={setVendorSearch}
                            placeholder="Search vendors..."
                        />
                        {isVendorsLoading && (
                            <Flex justify="center" py={2}>
                                <Spinner size="sm" color={checkboxMenuColors.brand} />
                            </Flex>
                        )}
                        <CheckboxMenuList maxH="220px">
                            {filteredVendorOptions.map((name) => (
                                <CheckboxMenuRow
                                    key={name || '__no_vendor__'}
                                    isSelected={selectedVendorNames.includes(name)}
                                    onClick={() => toggleVendor(name)}
                                >
                                    {name || 'No vendor'}
                                </CheckboxMenuRow>
                            ))}
                            {!isVendorsLoading && filteredVendorOptions.length === 0 && (
                                <Text fontSize="13px" color="gray.500" px="10px" py={2}>
                                    No vendors found
                                </Text>
                            )}
                        </CheckboxMenuList>
                    </>
                );

            case 'flaggedReasons':
                return (
                    <>
                        <Box px="6px" pt="6px" borderBottom="1px solid" borderColor="gray.100">
                            <MenuRow
                                kind="single"
                                isSelected={tempFilters.flaggedReasonsMatch !== 'exact'}
                                onClick={() => setFlaggedReasonsMatch('any')}
                            >
                                At least one of these
                            </MenuRow>
                            <MenuRow
                                kind="single"
                                isSelected={tempFilters.flaggedReasonsMatch === 'exact'}
                                onClick={() => setFlaggedReasonsMatch('exact')}
                            >
                                Only this combination
                            </MenuRow>
                        </Box>
                        <CheckboxMenuList maxH="220px">
                            {[...FLAGGED_REASON_OPTIONS]
                                .sort((a, b) => flaggedReasonLabel(a.key).localeCompare(
                                    flaggedReasonLabel(b.key),
                                    undefined,
                                    { sensitivity: 'base' },
                                ))
                                .map((reason) => (
                                <CheckboxMenuRow
                                    key={reason.key}
                                    isSelected={selectedFlaggedReasons.includes(reason.key)}
                                    onClick={() => toggleFlaggedReason(reason.key)}
                                >
                                    {flaggedReasonLabel(reason.key)}
                                </CheckboxMenuRow>
                            ))}
                        </CheckboxMenuList>
                    </>
                );

            case 'efficiencyFlags':
                return (
                    <CheckboxMenuList maxH="220px">
                        {EFFICIENCY_FLAG_OPTIONS.map((option) => (
                            <CheckboxMenuRow
                                key={option.value}
                                isSelected={(tempFilters.efficiencyFlags ?? []).includes(option.value)}
                                onClick={() => toggleEfficiencyFlag(option.value)}
                            >
                                {option.label}
                            </CheckboxMenuRow>
                        ))}
                    </CheckboxMenuList>
                );
        }
    };

    // Footer with Reset/Apply
    const renderFooter = (showReset: boolean = true) => (
        <MenuFooterActions
            secondaryLabel={showReset ? 'Reset' : 'Cancel'}
            onSecondary={showReset ? () => setTempFilters({}) : closePopover}
            onApply={handleApply}
        />
    );

    // Filtered category list for "Add filter" search
    const filteredCategories = FILTER_CATEGORIES.filter(cat =>
        !filterSearch || cat.label.toLowerCase().includes(filterSearch.toLowerCase())
    );

    return (
        <Box ref={containerRef}>
        <FilterBar>
            {activeFilters.map(cat => (
                <Popover
                    key={cat.key}
                    isOpen={openState === cat.key}
                    placement="bottom-start"
                    closeOnBlur={false}
                >
                    <PopoverTrigger>
                        <SelectionSummaryTooltip
                            items={getFilterTooltipItems(cat.key)}
                            isDisabled={openState === cat.key}
                            placement="bottom-start"
                        >
                            <FilterChip
                                icon={<Icon as={cat.icon as React.ElementType} boxSize={4} />}
                                label={getFilterLabel(cat.key)}
                                onRemove={() => removeFilter(cat.key)}
                                onClick={() => togglePopover(cat.key)}
                            />
                        </SelectionSummaryTooltip>
                    </PopoverTrigger>
                    <PopoverContent w="280px" sx={checkboxMenuPopoverContentSx} data-filter-popover>
                        <PopoverBody p={0}>
                            {renderFilterOptions(cat.key)}
                            {renderFooter(false)}
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            ))}

            <AddFilterMenu
                isOpen={openState === 'addFilter'}
                onToggle={() => togglePopover('addFilter')}
                width={addFilterView ? "280px" : "220px"}
                contentProps={{ 'data-filter-popover': '' }}
            >
                        {addFilterView === null ? (
                            <>
                                <CheckboxMenuSearch
                                    value={filterSearch}
                                    onChange={setFilterSearch}
                                    placeholder="Search filters..."
                                />

                                {/* Category list */}
                                <VStack align="stretch" spacing={0} p="6px">
                                    {filteredCategories.map(cat => (
                                        <MenuRow
                                            key={cat.key}
                                            kind="drillIn"
                                            summary={getCategorySummary(cat.key)}
                                            icon={<Icon as={cat.icon as React.ElementType} boxSize={4} />}
                                            onClick={() => setAddFilterView(cat.key)}
                                        >
                                            {cat.label}
                                            {cat.key === 'efficiencyFlags' ? <EfficiencyBreakdownInfoIcon /> : null}
                                        </MenuRow>
                                    ))}
                                </VStack>

                                {/* Reset / Apply */}
                                {renderFooter(true)}
                            </>
                        ) : (
                            <>
                                {/* Back button */}
                                <Flex align="center" px={2} py={2} borderBottom="1px solid" borderColor="gray.100">
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        onClick={() => setAddFilterView(null)}
                                        leftIcon={<Icon as={TbChevronLeft as React.ElementType} />}
                                        color="gray.600"
                                        fontWeight="medium"
                                    >
                                        {FILTER_CATEGORIES.find(c => c.key === addFilterView)?.label}
                                    </Button>
                                    {addFilterView === 'efficiencyFlags' ? <EfficiencyBreakdownInfoIcon /> : null}
                                </Flex>

                                {/* Filter options inline */}
                                {renderFilterOptions(addFilterView)}

                                {/* Reset / Apply */}
                                {renderFooter(true)}
                            </>
                        )}
            </AddFilterMenu>
            {(activeFilters.length > 0 || locationFilterActive) && (
                <ClearFiltersButton onClick={clearAllFilters} />
            )}
        </FilterBar>
        </Box>
    );
};

export default InvoiceAdvancedFiltersComponent;
